// features/settings/pages/TablesPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { SettingsModal } from '../components/SettingsModal';
import * as api from '../services/settingsApi';
import {
    Table2, Plus, Save, ArrowLeft, Users, MapPin, Pencil, Trash2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface Table {
    id: string;
    name: string;
    section: string | null;
    capacity: number;
    status: string;
}

interface Branch {
    id: string;
    name: string;
}

const STATUS_META: Record<string, { bg: string; text: string; border: string; dot: string }> = {
    available: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    occupied: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500' },
    reserved: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
    cleaning: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
};

export function TablesPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const [showForm, setShowForm] = useState(false);
    const [editingTable, setEditingTable] = useState<Table | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<Table | null>(null);
    const [form, setForm] = useState({ name: '', section: '', capacity: '4' });
    const [selectedBranch, setSelectedBranch] = useState('');

    const { data: branches } = useQuery<Branch[]>({
        queryKey: ['branches', user?.tenantId],
        queryFn: () => api.getBranches(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const { data: tables, isLoading, refetch } = useQuery<Table[]>({
        queryKey: ['tables', selectedBranch],
        queryFn: () => api.getTables(user!.tenantId, selectedBranch),
        enabled: !!user?.tenantId && !!selectedBranch,
    });

    const saveMutation = useMutation({
        mutationFn: (payload: any) => editingTable
            ? api.updateTable(user!.tenantId, editingTable.id, payload)
            : api.createTable(user!.tenantId, { ...payload, branchId: selectedBranch }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tables'] });
            addToast({ type: 'success', title: editingTable ? 'Table updated' : 'Table created' });
            closeForm();
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.message }),
    });

    const deleteMutation = useMutation({
        mutationFn: (tableId: string) => api.deleteTable(user!.tenantId, tableId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tables'] });
            setDeleteConfirm(null);
            addToast({ type: 'success', title: 'Table deleted' });
        },
    });

    const handleEdit = (table: Table) => {
        setEditingTable(table);
        setForm({ name: table.name, section: table.section || '', capacity: String(table.capacity) });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingTable(null);
        setForm({ name: '', section: '', capacity: '4' });
    };

    const availableTables = (tables || []).filter(t => t.status === 'available').length;
    const occupiedTables = (tables || []).filter(t => t.status === 'occupied').length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/settings')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg shadow-orange-500/25">
                                <Table2 className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Table Management</h1>
                                <p className="text-sm text-slate-500">
                                    {tables?.length || 0} table{(tables?.length || 0) !== 1 ? 's' : ''} · {availableTables} available · {occupiedTables} occupied
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => { closeForm(); setShowForm(true); }}
                            disabled={!selectedBranch}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl hover:from-orange-600 transition-all font-semibold shadow-lg text-sm disabled:opacity-50"
                            type="button"
                        >
                            <Plus className="w-4 h-4" /> Add Table
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Branch Selector */}
                <div className="max-w-xs">
                    <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Select Branch</label>
                    <select value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all">
                        <option value="">Choose a branch...</option>
                        {(branches || []).map((b: Branch) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                </div>

                {/* Content */}
                {!selectedBranch ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="text-center py-20 bg-white rounded-3xl border border-slate-200">
                        <div className="w-20 h-20 bg-orange-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <Table2 className="w-10 h-10 text-orange-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700">Select a branch</h3>
                        <p className="text-slate-400 mt-2">Choose a branch above to view and manage its tables.</p>
                    </motion.div>
                ) : (tables || []).length === 0 ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="text-center py-20 bg-white rounded-3xl border border-slate-200">
                        <div className="w-20 h-20 bg-orange-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <Table2 className="w-10 h-10 text-orange-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700">No tables in this branch</h3>
                        <p className="text-slate-400 mt-2">Add tables to this branch for restaurant management.</p>
                        <button onClick={() => { closeForm(); setShowForm(true); }}
                            className="mt-6 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-semibold shadow-lg inline-flex items-center gap-2">
                            <Plus className="w-5 h-5" /> Add First Table
                        </button>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                        {(tables || []).map((table, i) => {
                            const status = STATUS_META[table.status] || STATUS_META.available;
                            return (
                                <motion.div key={table.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.03 }}
                                    whileHover={{ y: -3, scale: 1.03 }}
                                    className="relative rounded-2xl border-2 transition-all duration-300 overflow-hidden bg-white shadow-sm hover:shadow-lg group">
                                    <div className={clsx('h-1', table.status === 'available' ? 'bg-emerald-500' : table.status === 'occupied' ? 'bg-rose-500' : 'bg-amber-500')} />
                                    <div className="p-3 text-center">
                                        <Table2 size={28} className={clsx('mx-auto mb-2', table.status === 'available' ? 'text-emerald-500' : table.status === 'occupied' ? 'text-rose-500' : 'text-amber-500')} />
                                        <p className="font-bold text-lg text-slate-900">{table.name}</p>
                                        {table.section && <p className="text-xs text-slate-500 flex items-center justify-center gap-1 mt-0.5"><MapPin size={10} /> {table.section}</p>}
                                        <p className="text-xs text-slate-400 flex items-center justify-center gap-1 mt-1"><Users size={12} /> {table.capacity}</p>
                                        <span className={clsx('inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-[10px] font-bold border', status.bg, status.text, status.border)}>
                                            <span className={clsx('w-1.5 h-1.5 rounded-full', status.dot)} />{table.status}
                                        </span>
                                        <div className="flex justify-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={(e) => { e.stopPropagation(); handleEdit(table); }}
                                                className="p-1 rounded-lg hover:bg-orange-50 text-slate-400 hover:text-orange-500"><Pencil size={12} /></button>
                                            <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(table); }}
                                                className="p-1 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500"><Trash2 size={12} /></button>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Create/Edit Modal */}
                <SettingsModal
                    open={showForm}
                    onClose={closeForm}
                    title={editingTable ? 'Edit Table' : 'Add Table'}
                    subtitle="Create a new restaurant table"
                    icon={Table2}
                    footer={
                        <>
                            <Button variant="secondary" onClick={closeForm}>Cancel</Button>
                            <Button leftIcon={<Save size={16} />} onClick={() => saveMutation.mutate({ ...form, capacity: parseInt(form.capacity) || 4 })}
                                isLoading={saveMutation.isPending} disabled={!form.name}>
                                {editingTable ? 'Update' : 'Save'}
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <Input label="Table Name/Number *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="T1" required />
                        <Input label="Section" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="Main Hall" leftIcon={<MapPin size={14} />} />
                        <Input label="Capacity" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} leftIcon={<Users size={14} />} />
                    </div>
                </SettingsModal>

                {/* Delete Confirmation */}
                <AnimatePresence>
                    {deleteConfirm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4"><Trash2 className="w-8 h-8 text-rose-500" /></div>
                                    <h2 className="text-xl font-bold text-slate-900">Delete "{deleteConfirm.name}"?</h2>
                                    <p className="text-sm text-slate-500 mt-2">This table will be permanently removed.</p>
                                </div>
                                <div className="flex gap-3 mt-8">
                                    <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm">Cancel</button>
                                    <button onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                                        className="flex-1 px-5 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-600 transition-all font-semibold text-sm shadow-lg shadow-rose-500/25">
                                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}